import { AWS_REGION, USER_POOL_ID, CLIENT_ID } from '../../config/env';
import { WebStorageStateStore } from 'oidc-client-ts';

// Get environment variables from centralized env config
const userPoolId = USER_POOL_ID;
const awsRegion = AWS_REGION;
const clientId = CLIENT_ID;
// Build redirect and logout URIs from current origin to ensure exact match with Cognito settings
const builtRedirectUri = `${window.location.origin}/oauth2/idpresponse`;
const builtPostLogoutUri = `${window.location.origin}/signout`;

// Create app-specific storage prefix to prevent conflicts between multiple apps/tabs
const APP_STORAGE_PREFIX = 'mentoringfusion_apply_';

// Create a custom storage wrapper that prefixes all keys
class PrefixedWebStorage implements Storage {
  private prefix: string;
  private storage: Storage;

  constructor(prefix: string, storage: Storage) {
    this.prefix = prefix;
    this.storage = storage;
  }

  get length(): number {
    return this.storage.length;
  }

  clear(): void {
    // Only clear keys with our prefix
    const keysToRemove: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key?.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => this.storage.removeItem(key));
  }

  getItem(key: string): string | null {
    return this.storage.getItem(this.prefix + key);
  }

  key(index: number): string | null {
    return this.storage.key(index);
  }

  removeItem(key: string): void {
    this.storage.removeItem(this.prefix + key);
  }

  setItem(key: string, value: string): void {
    this.storage.setItem(this.prefix + key, value);
  }
}

// Debug logging for production troubleshooting
if ((import.meta as any).env?.MODE === 'production') {
  console.log('OIDC Config Debug:', {
    userPoolId: userPoolId.substring(0, 8) + '...', // Only show first 8 chars for security
    awsRegion,
    clientId: clientId.substring(0, 8) + '...', // Only show first 8 chars for security
    redirectUri: builtRedirectUri,
    postLogoutRedirectUri: builtPostLogoutUri,
    currentUrl: window.location.href,
    userAgent: navigator.userAgent.substring(0, 50) + '...'
  });
}

const oidcConfig = {
  // Use proper AWS Cognito authority format
  authority: `https://cognito-idp.${awsRegion}.amazonaws.com/${userPoolId}`,
  client_id: clientId,
  redirect_uri: builtRedirectUri,
  post_logout_redirect_uri: builtPostLogoutUri,
  response_type: 'code', // This enables PKCE automatically
  scope: 'openid email profile',
  automaticSilentRenew: true,
  loadUserInfo: true,
  onSigninCallback: () => {
    // Clean up query params but do not navigate away. AuthRoute will redirect after auth completes.
    window.history.replaceState({}, document.title, window.location.pathname);
  },
  userStore: new WebStorageStateStore({ store: new PrefixedWebStorage(APP_STORAGE_PREFIX, window.localStorage) }),
};

export default oidcConfig;
